import type {
  ExpressionAst,
  JsonValue,
  ValidationAssertion,
  ValidationIssue,
} from './functional-control-flow';
import {
  astToCondition,
  conditionToAst,
  describeCondition,
  formatFieldPath,
  newRule,
  type Condition,
  type FieldPath,
} from './switch-condition-builder';
import {
  evaluateExpression,
  ExpressionEvaluationError,
  formatPath,
  jsonTypeOf,
} from './expression-eval';

/**
 * The Validate / Assert builder's model and its two-way mapping to the
 * backend's contract (`services/workflow_ir/validating.py`).
 *
 * Two faces, one contract: a human edits a field table (the bounded JSON
 * Schema subset) and rule rows (the assertions); the JSON they produce is
 * exactly what the node evaluates. The mapping is lossless for every keyword
 * the backend accepts, so "Edit as JSON" and the table always agree.
 */

// --- the schema subset -----------------------------------------------------

export type SchemaType =
  | 'object'
  | 'array'
  | 'string'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'null';

export const SCHEMA_TYPES: SchemaType[] = [
  'object',
  'array',
  'string',
  'number',
  'integer',
  'boolean',
  'null',
];

export const SCHEMA_TYPE_LABELS: Record<SchemaType, string> = {
  object: 'Object',
  array: 'List',
  string: 'Text',
  number: 'Number',
  integer: 'Whole number',
  boolean: 'True / false',
  null: 'Null',
};

/** Every keyword the backend's schema subset accepts (validating.py). */
export const SUPPORTED_SCHEMA_KEYWORDS = new Set([
  'type',
  'enum',
  'const',
  'properties',
  'required',
  'additionalProperties',
  'minProperties',
  'maxProperties',
  'items',
  'minItems',
  'maxItems',
  'minLength',
  'maxLength',
  'minimum',
  'maximum',
  'exclusiveMinimum',
  'exclusiveMaximum',
]);

/** Plain words for the keywords the backend rejects, so the hint says why. */
export const UNSUPPORTED_KEYWORD_HINTS: Record<string, string> = {
  $ref: 'references to other schemas',
  $defs: 'reusable definitions',
  definitions: 'reusable definitions',
  pattern: 'regular expressions',
  patternProperties: 'regular expressions on property names',
  format: 'formats such as email or date',
  contentEncoding: 'content decoders',
  contentMediaType: 'content decoders',
  if: 'conditional schemas',
  then: 'conditional schemas',
  else: 'conditional schemas',
  allOf: 'schema combinations',
  anyOf: 'schema combinations',
  oneOf: 'schema combinations',
  not: 'schema negation',
  uniqueItems: 'uniqueness checks',
  multipleOf: 'multiples',
  dependencies: 'property dependencies',
  dependentRequired: 'property dependencies',
  description: 'descriptions (keep them in the node description instead)',
  title: 'titles',
  default: 'defaults',
  examples: 'examples',
  $schema: 'schema dialect declarations',
  $id: 'schema identifiers',
};

export interface SchemaProperty {
  name: string;
  required: boolean;
  node: SchemaNode;
}

/** One schema node, keyword for keyword; `null`/`undefined` means "not set". */
export interface SchemaNode {
  type: SchemaType | null;
  enum: JsonValue[] | null;
  const: JsonValue | undefined;
  minimum?: number;
  maximum?: number;
  exclusiveMinimum?: number;
  exclusiveMaximum?: number;
  minLength?: number;
  maxLength?: number;
  minItems?: number;
  maxItems?: number;
  minProperties?: number;
  maxProperties?: number;
  additionalProperties?: boolean;
  /** Object properties, in authored order; null when `properties` is absent. */
  properties: SchemaProperty[] | null;
  /** Array item schema; null when `items` is absent. */
  items: SchemaNode | null;
  /** Keywords the backend will reject, kept so nothing is silently dropped. */
  unsupported: Record<string, JsonValue>;
}

export const emptySchemaNode = (
  type: SchemaType | null = null,
): SchemaNode => ({
  type,
  enum: null,
  const: undefined,
  properties: type === 'object' ? [] : null,
  items: null,
  unsupported: {},
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const numberOrUndefined = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

export const schemaToModel = (schema: unknown): SchemaNode => {
  const record = isRecord(schema) ? schema : {};
  const type = SCHEMA_TYPES.includes(record.type as SchemaType)
    ? (record.type as SchemaType)
    : null;
  const required = new Set(
    Array.isArray(record.required)
      ? (record.required as unknown[]).filter(
          (name): name is string => typeof name === 'string',
        )
      : [],
  );
  const properties: SchemaProperty[] | null = isRecord(record.properties)
    ? Object.entries(record.properties).map(([name, child]) => ({
        name,
        required: required.has(name),
        node: schemaToModel(child),
      }))
    : required.size > 0 || type === 'object'
      ? []
      : null;
  // `required` names with no matching property would be rejected by the
  // backend; keep them visible as empty properties so the author sees them.
  if (properties) {
    for (const name of required) {
      if (!properties.some((p) => p.name === name)) {
        properties.push({ name, required: true, node: emptySchemaNode() });
      }
    }
  }
  const unsupported: Record<string, JsonValue> = {};
  for (const [key, value] of Object.entries(record)) {
    if (!SUPPORTED_SCHEMA_KEYWORDS.has(key)) {
      unsupported[key] = value as JsonValue;
    }
  }
  return {
    type,
    enum: Array.isArray(record.enum) ? (record.enum as JsonValue[]) : null,
    const: 'const' in record ? (record.const as JsonValue) : undefined,
    minimum: numberOrUndefined(record.minimum),
    maximum: numberOrUndefined(record.maximum),
    exclusiveMinimum: numberOrUndefined(record.exclusiveMinimum),
    exclusiveMaximum: numberOrUndefined(record.exclusiveMaximum),
    minLength: numberOrUndefined(record.minLength),
    maxLength: numberOrUndefined(record.maxLength),
    minItems: numberOrUndefined(record.minItems),
    maxItems: numberOrUndefined(record.maxItems),
    minProperties: numberOrUndefined(record.minProperties),
    maxProperties: numberOrUndefined(record.maxProperties),
    additionalProperties:
      typeof record.additionalProperties === 'boolean'
        ? record.additionalProperties
        : undefined,
    properties,
    items: 'items' in record ? schemaToModel(record.items) : null,
    unsupported,
  };
};

export const modelToSchema = (node: SchemaNode): Record<string, JsonValue> => {
  const out: Record<string, JsonValue> = {};
  if (node.type) out.type = node.type;
  if (node.enum && node.enum.length > 0) out.enum = node.enum;
  if (node.const !== undefined) out.const = node.const;
  for (const key of [
    'minimum',
    'maximum',
    'exclusiveMinimum',
    'exclusiveMaximum',
    'minLength',
    'maxLength',
    'minItems',
    'maxItems',
    'minProperties',
    'maxProperties',
  ] as const) {
    const value = node[key];
    if (typeof value === 'number') out[key] = value;
  }
  if (typeof node.additionalProperties === 'boolean') {
    out.additionalProperties = node.additionalProperties;
  }
  if (node.properties && node.properties.length > 0) {
    const properties: Record<string, JsonValue> = {};
    const required: string[] = [];
    for (const property of node.properties) {
      if (!property.name) continue;
      properties[property.name] = modelToSchema(property.node);
      if (property.required) required.push(property.name);
    }
    out.properties = properties;
    if (required.length > 0) out.required = required;
  }
  if (node.items) out.items = modelToSchema(node.items);
  for (const [key, value] of Object.entries(node.unsupported)) {
    out[key] = value;
  }
  return out;
};

/**
 * Change a node's type and drop the bounds the backend would reject with it
 * ("schema keyword is incompatible with declared type"). `null` (any type)
 * keeps everything.
 */
export const coerceNodeToType = (
  node: SchemaNode,
  type: SchemaType | null,
): SchemaNode => {
  const next: SchemaNode = { ...node, type };
  if (type === null) return next;
  const numeric = type === 'number' || type === 'integer';
  if (type !== 'object') {
    next.properties = null;
    delete next.minProperties;
    delete next.maxProperties;
    delete next.additionalProperties;
  } else if (!next.properties) {
    next.properties = [];
  }
  if (type !== 'array') {
    next.items = null;
    delete next.minItems;
    delete next.maxItems;
  }
  if (type !== 'string') {
    delete next.minLength;
    delete next.maxLength;
  }
  if (!numeric) {
    delete next.minimum;
    delete next.maximum;
    delete next.exclusiveMinimum;
    delete next.exclusiveMaximum;
  }
  return next;
};

/** Keywords in a schema the backend would reject, with their locations. */
export const unsupportedKeywordsIn = (
  node: SchemaNode,
  path: string[] = [],
): Array<{ path: string; keyword: string; reason: string }> => {
  const out: Array<{ path: string; keyword: string; reason: string }> = [];
  for (const keyword of Object.keys(node.unsupported)) {
    out.push({
      path: path.join('.') || '(the value)',
      keyword,
      reason: UNSUPPORTED_KEYWORD_HINTS[keyword] ?? `the keyword "${keyword}"`,
    });
  }
  for (const property of node.properties ?? []) {
    out.push(...unsupportedKeywordsIn(property.node, [...path, property.name]));
  }
  if (node.items)
    out.push(...unsupportedKeywordsIn(node.items, [...path, '[]']));
  return out;
};

// --- building a schema from a sample ---------------------------------------

export interface InferOptions {
  /** Mark every present key as required (default true). */
  requireAll?: boolean;
  /** Stop descending below this depth (default 4). */
  maxDepth?: number;
  /** Stop after this many properties per object (default 64). */
  maxProperties?: number;
}

const inferType = (value: unknown): SchemaType => {
  switch (jsonTypeOf(value)) {
    case 'null':
      return 'null';
    case 'boolean':
      return 'boolean';
    case 'number':
      return Number.isInteger(value as number) ? 'integer' : 'number';
    case 'string':
      return 'string';
    case 'array':
      return 'array';
    default:
      return 'object';
  }
};

/**
 * The shape of a sample value as a schema: its type, its fields (all
 * required unless told otherwise), and the first item's shape for lists.
 * No values are copied in; enums and bounds are the author's choice.
 */
export const inferSchemaFromSample = (
  sample: unknown,
  options: InferOptions = {},
): SchemaNode => {
  const requireAll = options.requireAll ?? true;
  const maxDepth = options.maxDepth ?? 4;
  const maxProperties = options.maxProperties ?? 64;
  const walk = (value: unknown, depth: number): SchemaNode => {
    const node = emptySchemaNode(inferType(value));
    if (depth >= maxDepth) {
      node.properties = node.type === 'object' ? [] : null;
      return node;
    }
    if (isRecord(value)) {
      node.properties = Object.entries(value)
        .slice(0, maxProperties)
        .map(([name, child]) => ({
          name,
          required: requireAll,
          node: walk(child, depth + 1),
        }));
    } else if (Array.isArray(value) && value.length > 0) {
      node.items = walk(value[0], depth + 1);
    }
    return node;
  };
  return walk(sample, 0);
};

// --- assertions as rows ----------------------------------------------------

export interface AssertionRow {
  code: string;
  message: string;
  /** Where the error points when the rule fails (empty = the whole value). */
  path: FieldPath;
  condition: Condition;
}

export const assertionsToRows = (
  assertions: ValidationAssertion[],
): AssertionRow[] =>
  assertions.map((assertion) => ({
    code: assertion.code,
    message: assertion.message,
    path: assertion.path,
    condition: astToCondition(assertion.when),
  }));

export const rowsToAssertions = (rows: AssertionRow[]): ValidationAssertion[] =>
  rows.map((row) => ({
    code: row.code,
    message: row.message,
    path: row.path,
    when: conditionToAst(row.condition),
  }));

const IDENTIFIER_START = /^[A-Za-z]/;
const IDENTIFIER_BODY = /[^A-Za-z0-9_.-]+/g;

/** Turn free text into a valid assertion code (`^[A-Za-z][A-Za-z0-9_.-]*$`). */
export const toIdentifier = (text: string, fallback = 'check'): string => {
  const cleaned = text
    .trim()
    .replace(IDENTIFIER_BODY, '_')
    .replace(/^_+|_+$/g, '');
  if (!cleaned) return fallback;
  return IDENTIFIER_START.test(cleaned) ? cleaned : `${fallback}_${cleaned}`;
};

/** A code suggested from the rule itself, e.g. `rating_total.at_least`. */
export const suggestAssertionCode = (
  condition: Condition,
  existing: readonly string[],
): string => {
  let base = 'check';
  if (condition.kind === 'rule') {
    const field = condition.field.length
      ? condition.field.map(String).join('_')
      : 'value';
    base = toIdentifier(`${field}.${condition.op}`);
  } else if (condition.kind === 'group') {
    base = condition.op === 'and' ? 'all_of' : 'any_of';
  }
  if (!existing.includes(base)) return base;
  let n = 2;
  while (existing.includes(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
};

/** A message suggested from the rule, e.g. "rating.total is at least 5". */
export const suggestAssertionMessage = (condition: Condition): string => {
  const described = describeCondition(condition);
  if (!described || described === 'always')
    return 'The value must pass this check';
  return `Expected: ${described}`;
};

export const newAssertionRow = (
  existingCodes: readonly string[],
): AssertionRow => {
  const condition = newRule();
  return {
    code: suggestAssertionCode(condition, existingCodes),
    message: 'The value must pass this check',
    path: [],
    condition,
  };
};

// --- previewing the checks against a sample --------------------------------

export type CheckOutcome =
  | { kind: 'pass' }
  | { kind: 'fail'; issues: ValidationIssue[] }
  | { kind: 'error'; message: string };

export interface ValidationPreview {
  kind: 'unknown' | 'ok' | 'issues' | 'error';
  /** Every issue the backend would record (schema first, then rules). */
  issues: ValidationIssue[];
  /** Per assertion row, in order. */
  assertionOutcomes: CheckOutcome[];
  /** The schema's outcome, when a schema is set. */
  schemaOutcome: CheckOutcome | null;
  /** A plain sentence for the banner. */
  message: string;
}

type Issue = ValidationIssue;

const strictEqual = (left: unknown, right: unknown): boolean => {
  if (jsonTypeOf(left) !== jsonTypeOf(right)) return false;
  if (Array.isArray(left) && Array.isArray(right)) {
    return (
      left.length === right.length &&
      left.every((item, i) => strictEqual(item, right[i]))
    );
  }
  if (isRecord(left) && isRecord(right)) {
    const leftKeys = Object.keys(left).sort();
    const rightKeys = Object.keys(right).sort();
    return (
      leftKeys.length === rightKeys.length &&
      leftKeys.every((key, i) => key === rightKeys[i]) &&
      leftKeys.every((key) => strictEqual(left[key], right[key]))
    );
  }
  return left === right;
};

const typeMatches = (value: unknown, expected: string): boolean => {
  switch (expected) {
    case 'null':
      return value === null;
    case 'boolean':
      return typeof value === 'boolean';
    case 'integer':
      return typeof value === 'number' && Number.isInteger(value);
    case 'number':
      return typeof value === 'number';
    case 'string':
      return typeof value === 'string';
    case 'array':
      return Array.isArray(value);
    case 'object':
      return isRecord(value);
    default:
      return true;
  }
};

/** Mirror of `validating.walk_schema`: the issues the backend would record. */
export const schemaIssues = (
  schema: Record<string, JsonValue>,
  value: unknown,
  path: Array<string | number> = [],
): Issue[] => {
  const issues: Issue[] = [];
  const issue = (
    keyword: string,
    at: Array<string | number>,
    message: string,
  ) =>
    issues.push({
      code: `validation.schema.${keyword}`,
      path: at,
      message,
      source: 'schema',
      keyword,
    });
  const expected = schema.type;
  if (typeof expected === 'string' && !typeMatches(value, expected)) {
    issue('type', path, `Value must be of JSON type ${expected}`);
    return issues;
  }
  if (
    Array.isArray(schema.enum) &&
    !schema.enum.some((allowed) => strictEqual(value, allowed))
  ) {
    issue('enum', path, 'Value is not one of the allowed choices');
  }
  if ('const' in schema && !strictEqual(value, schema.const)) {
    issue('const', path, 'Value does not match the required constant');
  }
  if (isRecord(value)) {
    const required = Array.isArray(schema.required)
      ? [...(schema.required as string[])].sort()
      : [];
    for (const name of required) {
      if (!(name in value)) {
        issue('required', [...path, name], 'Required property is missing');
      }
    }
    const properties = isRecord(schema.properties) ? schema.properties : {};
    if (schema.additionalProperties === false) {
      for (const name of Object.keys(value)
        .filter((key) => !(key in properties))
        .sort()) {
        issue(
          'additionalProperties',
          [...path, name],
          'Additional property is not allowed',
        );
      }
    }
    for (const name of Object.keys(properties).sort()) {
      if (name in value) {
        issues.push(
          ...schemaIssues(
            properties[name] as Record<string, JsonValue>,
            value[name],
            [...path, name],
          ),
        );
      }
    }
    const length = Object.keys(value).length;
    if (
      typeof schema.minProperties === 'number' &&
      length < schema.minProperties
    ) {
      issue('minProperties', path, 'Object has too few properties');
    }
    if (
      typeof schema.maxProperties === 'number' &&
      length > schema.maxProperties
    ) {
      issue('maxProperties', path, 'Object has too many properties');
    }
  }
  if (Array.isArray(value)) {
    if (isRecord(schema.items)) {
      value.forEach((item, index) =>
        issues.push(
          ...schemaIssues(schema.items as Record<string, JsonValue>, item, [
            ...path,
            index,
          ]),
        ),
      );
    }
    if (typeof schema.minItems === 'number' && value.length < schema.minItems) {
      issue('minItems', path, 'Array has too few items');
    }
    if (typeof schema.maxItems === 'number' && value.length > schema.maxItems) {
      issue('maxItems', path, 'Array has too many items');
    }
  }
  if (typeof value === 'string') {
    if (
      typeof schema.minLength === 'number' &&
      value.length < schema.minLength
    ) {
      issue('minLength', path, 'String is too short');
    }
    if (
      typeof schema.maxLength === 'number' &&
      value.length > schema.maxLength
    ) {
      issue('maxLength', path, 'String is too long');
    }
  }
  if (typeof value === 'number') {
    if (typeof schema.minimum === 'number' && value < schema.minimum) {
      issue('minimum', path, 'Number is below minimum');
    }
    if (typeof schema.maximum === 'number' && value > schema.maximum) {
      issue('maximum', path, 'Number is above maximum');
    }
    if (
      typeof schema.exclusiveMinimum === 'number' &&
      value <= schema.exclusiveMinimum
    ) {
      issue('exclusiveMinimum', path, 'Number is not above exclusive minimum');
    }
    if (
      typeof schema.exclusiveMaximum === 'number' &&
      value >= schema.exclusiveMaximum
    ) {
      issue('exclusiveMaximum', path, 'Number is not below exclusive maximum');
    }
  }
  return issues;
};

export interface PreviewConfig {
  schema: Record<string, JsonValue> | null;
  assertions: ValidationAssertion[];
}

/**
 * What the backend's `validate_value` would do with `value` (`undefined` =
 * no sample available): the issues it would record, per-check outcomes for
 * the rows, and a sentence for the banner.
 */
export const previewValidation = (
  config: PreviewConfig,
  value: JsonValue | undefined,
  upstreamName?: string | null,
): ValidationPreview => {
  const none: ValidationPreview = {
    kind: 'unknown',
    issues: [],
    assertionOutcomes: [],
    schemaOutcome: null,
    message: '',
  };
  if (value === undefined) return none;
  const issues: Issue[] = [];
  let schemaOutcome: CheckOutcome | null = null;
  if (config.schema) {
    const found = schemaIssues(config.schema, value);
    issues.push(...found);
    schemaOutcome = found.length
      ? { kind: 'fail', issues: found }
      : { kind: 'pass' };
  }
  const assertionOutcomes: CheckOutcome[] = [];
  for (const [index, assertion] of config.assertions.entries()) {
    let passed: unknown;
    try {
      passed = evaluateExpression(assertion.when, { value });
    } catch (error) {
      const detail =
        error instanceof ExpressionEvaluationError
          ? error.message
          : 'the condition could not be evaluated.';
      const message = `Rule ${index + 1} ("${assertion.code}") would fail the run: ${detail}`;
      assertionOutcomes.push({ kind: 'error', message });
      return {
        kind: 'error',
        issues,
        assertionOutcomes,
        schemaOutcome,
        message,
      };
    }
    if (typeof passed !== 'boolean') {
      const message = `Rule ${index + 1} ("${assertion.code}") does not produce true or false.`;
      assertionOutcomes.push({ kind: 'error', message });
      return {
        kind: 'error',
        issues,
        assertionOutcomes,
        schemaOutcome,
        message,
      };
    }
    if (passed) {
      assertionOutcomes.push({ kind: 'pass' });
    } else {
      const issue: Issue = {
        code: assertion.code,
        path: assertion.path,
        message: assertion.message,
        source: 'assertion',
        keyword: assertion.code,
      };
      issues.push(issue);
      assertionOutcomes.push({ kind: 'fail', issues: [issue] });
    }
  }
  const total = config.assertions.length + (config.schema ? 1 : 0);
  const from = upstreamName ? ` from ${upstreamName}` : '';
  if (total === 0) {
    return {
      kind: 'ok',
      issues,
      assertionOutcomes,
      schemaOutcome,
      message: `Nothing to check yet: add a shape or a rule.`,
    };
  }
  if (issues.length === 0) {
    return {
      kind: 'ok',
      issues,
      assertionOutcomes,
      schemaOutcome,
      message: `With the last value${from}, all ${total} check${total === 1 ? '' : 's'} pass.`,
    };
  }
  const failedChecks =
    assertionOutcomes.filter((o) => o.kind === 'fail').length +
    (schemaOutcome?.kind === 'fail' ? 1 : 0);
  return {
    kind: 'issues',
    issues,
    assertionOutcomes,
    schemaOutcome,
    message: `With the last value${from}, ${failedChecks} of ${total} check${total === 1 ? '' : 's'} would fail (${issues.length} error${issues.length === 1 ? '' : 's'} recorded).`,
  };
};

// --- reading a result ------------------------------------------------------

/** One error from the node's `errors` list, in plain words. */
export const describeValidationIssue = (issue: ValidationIssue): string => {
  const where = formatPath(issue.path);
  if (issue.source === 'schema') {
    return `${issue.message} (${where})`;
  }
  return issue.path.length ? `${issue.message} (${where})` : issue.message;
};

export interface ValidationSummary {
  /** null when the result is not a Validate result. */
  isValid: boolean | null;
  errorCount: number;
  errorsTruncated: boolean;
  issues: ValidationIssue[];
  /** The backend's own sentence when present (Phase 2 adds it), else ours. */
  summary: string;
  /** Set when the node failed before checking (bad config, missing input). */
  runtimeError: string | null;
}

/**
 * Read a Validate node's result (`metadata.validation`) into words. Works
 * for collect mode (`is_valid` + `errors`) and fail mode (the run stopped).
 */
export const summarizeValidationResult = (
  result: unknown,
  issuesFrom: (result: unknown) => ValidationIssue[],
): ValidationSummary | null => {
  const root = isRecord(result) ? result : null;
  const metadata = isRecord(root?.metadata) ? root.metadata : null;
  const validation = isRecord(metadata?.validation)
    ? metadata.validation
    : null;
  if (!validation) return null;
  const issues = issuesFrom(result);
  if (validation.kind === 'validation_runtime_error') {
    const details = isRecord(validation.error_details)
      ? validation.error_details
      : null;
    return {
      isValid: null,
      errorCount: 0,
      errorsTruncated: false,
      issues: [],
      summary: 'The checks could not run.',
      runtimeError:
        (typeof root?.error === 'string' && root.error) ||
        (typeof details?.code === 'string' ? details.code : 'unknown error'),
    };
  }
  const isValid =
    typeof validation.is_valid === 'boolean' ? validation.is_valid : null;
  const errorCount =
    typeof validation.error_count === 'number'
      ? validation.error_count
      : issues.length;
  const truncated = validation.errors_truncated === true;
  const summary =
    typeof validation.summary === 'string' && validation.summary
      ? validation.summary
      : isValid
        ? 'All checks passed.'
        : `${errorCount} check${errorCount === 1 ? '' : 's'} failed${
            issues.length
              ? `: ${[...new Set(issues.map((i) => i.code))].slice(0, 5).join(', ')}`
              : ''
          }.`;
  return {
    isValid,
    errorCount,
    errorsTruncated: truncated,
    issues,
    summary,
    runtimeError: null,
  };
};

export const formatSchemaPath = formatFieldPath;
export type { ExpressionAst };
